import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Loader2, LogIn, UserPlus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BrandHeader from "@/components/BrandHeader";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "h-12 rounded-none bg-[#111] border-white/15 text-white placeholder:text-white/30 focus-visible:ring-1 focus-visible:ring-brand-orange";

export default function AuthPage({ mode: initialMode = "login" }) {
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const next = new URLSearchParams(location.search).get("next") || "/mein-verlauf";
  const isRegister = mode === "register";

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = isRegister
        ? await register(form.email, form.password, form.name)
        : await login(form.email, form.password);
      toast.success(isRegister ? "Konto erstellt." : "Willkommen zurück.");
      navigate(data.role === "admin" ? "/admin" : next);
    } catch (err) {
      setError(err?.response?.data?.detail || "Es ist ein Fehler aufgetreten.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative z-10">
      <BrandHeader minimal />
      <div className="max-w-md mx-auto px-6 py-16 sm:py-24">
        <div className="inline-flex items-center gap-2 border border-brand-orange/40 bg-brand-orange/10 px-3 py-1.5 mb-6">
          {isRegister ? <UserPlus className="h-3.5 w-3.5 text-brand-orange" /> : <LogIn className="h-3.5 w-3.5 text-brand-orange" />}
          <span className="text-xs tracking-[0.2em] uppercase text-brand-orange">
            {isRegister ? "Konto erstellen" : "Anmelden"}
          </span>
        </div>
        <h1 className="font-display uppercase text-3xl tracking-tight text-white">
          {isRegister ? "Ihren Verlauf sichern" : "Willkommen zurück"}
        </h1>
        <p className="mt-3 text-sm text-white/50 leading-relaxed">
          {isRegister
            ? "Mit einem Konto speichern Sie Ihre Checks und sehen die Entwicklung Ihrer Finanz-Tragfähigkeit über die Zeit."
            : "Melden Sie sich an, um Ihre gespeicherten Checks und Ihren Verlauf zu sehen."}
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="auth-form">
          {isRegister && (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-white/50">Name (optional)</Label>
              <Input data-testid="auth-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="Vor- und Nachname" />
            </div>
          )}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-white/50">E-Mail</Label>
            <Input data-testid="auth-email" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="name@firma.de" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-white/50">Passwort</Label>
            <Input data-testid="auth-password" type="password" value={form.password} onChange={set("password")} className={inputCls} placeholder={isRegister ? "mind. 8 Zeichen" : "••••••••"} />
          </div>
          {error && <p className="text-xs text-red-400" data-testid="auth-error">{error}</p>}
          <Button type="submit" data-testid="auth-submit-btn" disabled={loading}
            className="w-full h-12 rounded-none bg-brand-orange text-black hover:bg-white font-bold uppercase tracking-wider text-sm transition-colors">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : isRegister ? "Konto erstellen" : "Anmelden"}
          </Button>
        </form>

        <button
          data-testid="auth-toggle-mode"
          onClick={() => { setMode(isRegister ? "login" : "register"); setError(""); }}
          className="mt-6 text-sm text-white/50 hover:text-brand-orange transition-colors"
        >
          {isRegister ? "Schon ein Konto? Jetzt anmelden" : "Noch kein Konto? Jetzt erstellen"}
        </button>

        <div className="mt-8 pt-6 border-t border-white/10">
          <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-white/40 hover:text-brand-orange transition-colors">
            <ArrowLeft className="h-4 w-4" /> Zur Startseite
          </Link>
        </div>
      </div>
    </div>
  );
}
